import React, { useState } from 'react';
import { Stack, Button, Typography } from '@mui/material';
import { useQuery, useMutation } from '@apollo/client';
import { EventPlan } from '../../components/EventPlan';
import { EventPlanInvitation } from './EventPlanInvitation';
import {
  GET_EVENT_PLANS,
  GET_EVENT_PLAN_INVITATIONS,
  CREATE_EVENT_PLAN,
} from './graphql';
import { ConfirmEmailBanner } from './ConfirmEmailBanner';
import { CreateEventPlanModal } from './CreateEventPlanModal';
import { useAuth } from '../../contexts/AuthContext';
import { LoadingIndicator } from '@pv/common/components';
import {
  GetEventPlanInvitationsQuery,
  GetEventPlanInvitationsQueryVariables,
  GetEventPlansQuery,
  GetEventPlansQueryVariables,
} from '@pv/common/graphql/operations';

const mapQueryToEventPlan = (
  eventPlan: NonNullable<
    NonNullable<GetEventPlansQuery['authenticatedGuest']>['eventPlans']
  >[number],
) => ({
  ...eventPlan,
  startDate: eventPlan.startDate ? new Date(eventPlan.startDate) : undefined,
  startOffset: eventPlan.startOffset || undefined,
  endOffset: eventPlan.endOffset || undefined,
  location: eventPlan.location || undefined,
  groupSize: eventPlan.groupSize || undefined,
  name: eventPlan.name || undefined,
  guest: eventPlan.guest,
  collaborators: eventPlan.collaborators.map((collaborator) => ({
    ...collaborator,
    fullName: collaborator.fullName || undefined,
    gravatarUrl: collaborator.gravatarUrl || undefined,
  })),
  invitations: eventPlan.invitations,
  events: eventPlan.events.map((event) => ({
    ...event,
    inquiryStatus: event.inquiryStatus || undefined,
    planner: {
      fullName: event.planner?.fullName || undefined,
    },
    spaces:
      event.spaces?.map((space) => ({
        ...space,
        images: space.images.filter(
          (image): image is { url: string } => !!image.url,
        ),
      })) || [],
    venue: {
      ...event.venue,
      logo: event.venue.logo || undefined,
    },
  })),
});

export const EventPlansPage = () => {
  const { guest } = useAuth();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const { data, loading, error } = useQuery<
    GetEventPlansQuery,
    GetEventPlansQueryVariables
  >(GET_EVENT_PLANS, {
    skip: !guest?.id,
  });
  const {
    data: invitationsData,
    loading: invitationsLoading,
    error: invitationsError,
  } = useQuery<
    GetEventPlanInvitationsQuery,
    GetEventPlanInvitationsQueryVariables
  >(GET_EVENT_PLAN_INVITATIONS, {
    fetchPolicy: 'cache-and-network',
    skip: !guest?.id || !guest?.confirmed,
  });

  const [createEventPlan] = useMutation(CREATE_EVENT_PLAN, {
    update: (cache, { data }) => {
      const newEventPlan = data?.createEventPlan?.eventPlan;
      if (newEventPlan) {
        const existingData = cache.readQuery<GetEventPlansQuery>({
          query: GET_EVENT_PLANS,
        });
        if (existingData && existingData.authenticatedGuest) {
          cache.writeQuery({
            query: GET_EVENT_PLANS,
            data: {
              authenticatedGuest: {
                ...existingData.authenticatedGuest,
                eventPlans: [
                  ...existingData.authenticatedGuest.eventPlans,
                  newEventPlan,
                ],
              },
            },
          });
        }
      }
    },
  });

  if (loading || invitationsLoading) return <LoadingIndicator />;
  if (error) return <div>Error: {error.message}</div>;
  if (invitationsError) return <div>Error: {invitationsError.message}</div>;

  const invitations = invitationsData?.eventPlanInvitations || [];
  const eventPlans = data?.authenticatedGuest?.eventPlans || [];

  return (
    <Stack
      direction="column"
      gap={3}
      padding={3}
      maxWidth="900px"
      margin="0 auto"
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h4" component="h1">
          My Events
        </Typography>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => setIsCreateModalOpen(true)}
        >
          Create New Event
        </Button>
      </Stack>
      {guest && !guest.confirmed && <ConfirmEmailBanner />}
      {invitations.length > 0 && (
        <Stack spacing={1}>
          {invitations.map((invitation) => (
            <EventPlanInvitation key={invitation.id} invitation={invitation} />
          ))}
        </Stack>
      )}
      {eventPlans.map((eventPlan) => (
        <EventPlan
          key={eventPlan.id}
          eventPlan={mapQueryToEventPlan(eventPlan)}
        />
      ))}
      <CreateEventPlanModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreate={(eventPlan) => {
          createEventPlan({ variables: { input: eventPlan } });
        }}
      />
    </Stack>
  );
};
